/**
 * BulkOutreachSheet — opens a bottom-sheet / dialog that lets an operator:
 *   1. Filter the full vendor list by Location (free-text) + Vendor Type
 *      (Hotel / Driver / Guide / Transport / Activity / Agent).
 *   2. Multi-select vendors.
 *   3. Compose a single subject + body. Signature is auto-appended.
 *   4. Fire WhatsApp (sequential wa.me opens) and/or Email (single mail
 *      with BCC) and log every contact to /api/pollen/outreach.
 *
 * Used both from the Query workspace (queryId prop set → attaches each
 * vendor to the query) and from a standalone "Bulk Outreach" entry-point
 * on the Vendors page (queryId left null).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { API, useAuth } from '@/App';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Mail, Megaphone, Send, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useOperatorName, pollenSignature } from '@/hooks/useOperatorName';
import { personaliseEmailBody, personaliseEmailBodyMulti, personaliseWhatsAppText } from '@/lib/outreachPersonalise';

// Labels surfaced in the bulk outreach UI map to the underlying vendor
// `type` values stored in the DB. "Operators" → Transport so that fleet
// operators (taxi companies, tour operators) are reachable from the same
// dropdown without confusing the operator with multiple DB types.
const VENDOR_TYPE_OPTIONS = [
  { label: 'Hotels', value: 'Hotel' },
  { label: 'Guides', value: 'Guide' },
  { label: 'Drivers', value: 'Driver' },
  { label: 'Operators', value: 'Transport' },
];

// Same 6-tier taxonomy used in the Hotel Requirements card on Query
// Workspace. Surfaced as a filter chip-row when Type=Hotel so the
// operator only sees vendors that match the requested accommodation.
const ACCOMMODATION_TYPES = ['Hotel', 'Resort', 'Villa', 'Boutique', 'Homestay', 'Luxury Chain'];

const splitContacts = (s) => (s || '').split(/[,;]/).map(x => x.trim()).filter(Boolean);
const digitsOnly = (s) => (s || '').replace(/\D+/g, '');
// Maps internal vendor type values back to their plural UI label so the
// "what just went out" banner reads naturally ("Sent to 5 Hotels").
const typeLabel = (val) => {
  const found = VENDOR_TYPE_OPTIONS.find(o => o.value === val);
  return found ? found.label : (val || 'all types');
};

export default function BulkOutreachSheet({
  open,
  onOpenChange,
  queryId = null,
  defaultType = '',
  defaultLocation = '',
  defaultLocationAliases = [],     // OR-match locations: ['Cherrapunjee','Sohra']
  defaultAccommodationTypes = [],  // pre-fill hotel accommodation filter
  defaultSubject = '',
  defaultBody = '',
  preselectedVendorIds = null,     // [] of vendor ids → compact mode
  attachments = [],                // downloadable customer/itinerary docs;
                                   // never auto-included — operator clicks
                                   // "Attach documents" inside the sheet.
  shareBase = 'https://pollendots.com',  // branded public-share host
  followUpMode = false,            // single-recipient TO (no BCC chain)
  onSent,
}) {
  // Compact mode = vendors were already chosen inside a requirement card.
  // Hide all filters/search/full-vendor-list; show only the pre-selected
  // vendors, the subject + body, and the two send buttons.
  const compactMode = Array.isArray(preselectedVendorIds);
  const { user } = useAuth();
  const operatorName = useOperatorName(user?.email, user?.name);
  const [type, setType] = useState(defaultType);
  const [location, setLocation] = useState(defaultLocation);
  const [locationAliases, setLocationAliases] = useState(defaultLocationAliases);
  const [accommodationFilter, setAccommodationFilter] = useState(defaultAccommodationTypes);
  const [vendors, setVendors] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultBody);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  // Strip surfaced after each successful batch so the operator knows what
  // was just fired AND that they can immediately switch type and re-run.
  const [lastSent, setLastSent] = useState(null); // { channel, count, type }
  // Gmail Mailbox connection — when connected, Email sends ONE personalised
  // mail per vendor via team@pollendots.com (property name auto-added for
  // hotels) instead of a single shared BCC draft.
  const [mailboxReady, setMailboxReady] = useState(false);
  // Season / month / year for standalone B2B rate requests (Vendors page
  // entry — no query attached, often no fixed dates).
  const [season, setSeason] = useState('');

  useEffect(() => {
    if (!open) return;
    const t = localStorage.getItem('session_token');
    fetch(`${API}/admin/mailbox/settings`, {
      credentials: 'include',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setMailboxReady(!!(d && d.configured && d.status === 'connected')))
      .catch(() => setMailboxReady(false));
  }, [open]);

  // Stable string keys so parents that inline-construct these arrays
  // (defaultLocationAliases={[]}, defaultAccommodationTypes={[]}) don't
  // cause an infinite setState loop on open.
  const aliasesKey = JSON.stringify(defaultLocationAliases || []);
  const accTypesKey = JSON.stringify(defaultAccommodationTypes || []);
  const preselectedKey = JSON.stringify(preselectedVendorIds || null);
  useEffect(() => {
    if (!open) return;
    setType(defaultType);
    setLocation(defaultLocation);
    setLocationAliases(defaultLocationAliases || []);
    setAccommodationFilter(defaultAccommodationTypes || []);
    setSubject(defaultSubject || (queryId ? 'Quote request — Pollen Dots' : 'Pollen Dots — request'));
    setMessage(defaultBody);
    // In compact mode, pre-pick exactly the vendor IDs chosen inside the
    // card. The operator can still untick to skip individual vendors before
    // sending.
    setPicked(compactMode ? new Set(preselectedVendorIds) : new Set());
    setSearch('');
    setSeason('');
    setLastSent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultType, defaultLocation, aliasesKey, accTypesKey, defaultSubject, defaultBody, queryId, compactMode, preselectedKey]);

  // Fetch the full vendor list once when the sheet opens — we filter
  // client-side because the dataset is well under 1k.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = localStorage.getItem('session_token');
    fetch(`${API}/pollen/vendors`, {
      credentials: 'include',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    })
      .then(r => r.ok ? r.json() : [])
      .then(d => setVendors(Array.isArray(d) ? d : []))
      .catch(() => setVendors([]))
      .finally(() => setLoading(false));
  }, [open]);

  // Filter by type + location (with optional aliases) + accommodation type
  // (hotels only) + free-text search. All client-side. In compact mode we
  // ignore every filter and only show the pre-selected vendors.
  const filtered = useMemo(() => {
    if (compactMode) {
      const allow = new Set(preselectedVendorIds || []);
      return vendors.filter(v => allow.has(v.id));
    }
    const t = (type || '').toLowerCase();
    const allLocs = [location, ...(locationAliases || [])]
      .map(s => (s || '').toLowerCase().trim()).filter(Boolean);
    const accSet = new Set((accommodationFilter || []).map(s => s.toLowerCase()));
    const q = (search || '').toLowerCase().trim();
    return vendors.filter(v => {
      if (t && (v.type || '').toLowerCase() !== t) return false;
      if (allLocs.length) {
        const hay = `${v.location || ''} ${v.address || ''}`.toLowerCase();
        const hit = allLocs.some(loc => hay.includes(loc));
        if (!hit) return false;
      }
      // Accommodation-type filter only meaningful for Hotel vendors. A
      // single property can carry MULTIPLE types now (Boutique + Resort,
      // Homestay + Boutique, …). Match if any of the vendor's types is in
      // the picked filter set; vendors with no types set are still shown.
      if (t === 'hotel' && accSet.size > 0) {
        const vTypes = (v.accommodation_types && v.accommodation_types.length)
          ? v.accommodation_types
          : (v.accommodation_type ? [v.accommodation_type] : []);
        if (vTypes.length > 0) {
          const has = vTypes.some(x => accSet.has((x || '').toLowerCase()));
          if (!has) return false;
        }
      }
      if (q) {
        const hay = `${v.name || ''} ${v.location || ''} ${v.address || ''} ${v.email || ''} ${v.phone || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [vendors, type, location, locationAliases, accommodationFilter, search]);

  const toggle = (id) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (picked.size === filtered.length) setPicked(new Set());
    else setPicked(new Set(filtered.map(v => v.id)));
  };

  const signedBody = (raw) => `${(raw || '').trim()}\n\n${pollenSignature(operatorName)}`;

  // One-tap B2B rate-request template from the current Location filter +
  // season/period. Property names are personalised per hotel at send time.
  const fillRateTemplate = () => {
    const loc = (location || '').trim();
    const per = (season || '').trim();
    const lines = ['Hello,', '', 'Greetings from Pollen Dots.', '',
      'We would like to request your best B2B rates with the following details:', ''];
    if (loc) lines.push(`• Location: ${loc}`);
    lines.push(per
      ? `• Stay period: ${per} — please share your best B2B seasonal rates for this period`
      : '• Stay dates: Not fixed — please share your best B2B rates for the season / year');
    lines.push('', 'Please share your best B2B rate, inclusions, and cancellation policy.', '', 'Looking forward.');
    setMessage(lines.join('\n'));
    setSubject(`B2B rate request${loc ? ` — ${loc}` : ''}${per ? ` | ${per}` : ''} — Pollen Dots`);
    toast.success('Rate-request template filled — edit as needed before sending.');
  };

  const log = async (vendor, channel, recipient) => {
    const t = localStorage.getItem('session_token');
    try {
      await fetch(`${API}/pollen/outreach`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify({
          vendor_id: vendor.id,
          vendor_name: vendor.name || '',
          vendor_type: vendor.type || '',
          channel,
          subject: channel !== 'whatsapp' ? subject : '',
          message: signedBody(message),
          recipient,
          query_id: queryId || null,
        }),
      });
    } catch { /* journal-only */ }
  };

  const fireEmailBulk = async () => {
    const targets = filtered.filter(v => picked.has(v.id));
    if (!targets.length) { toast.error('Pick at least one vendor'); return; }
    if (!message.trim()) { toast.error('Type a message first'); return; }
    setSending(true);
    // Follow-up mode: single recipient on the TO line so the vendor's reply
    // never goes to the wider BCC chain from the original outreach. Email
    // clients still thread on subject so the conversation stays in-chain.
    if (followUpMode && targets.length === 1) {
      const v = targets[0];
      const to = (splitContacts(v.email) || [])[0];
      if (!to) { setSending(false); toast.error('Vendor has no email on file.'); return; }
      const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(personaliseEmailBody(signedBody(message), v))}`;
      window.location.href = url;
      await log(v, 'email', to);
      toast.success(`Follow-up opened to ${v.name}`);
      setSending(false);
      onSent && onSent({ channel: 'email', count: 1, noEmail: 0, followUp: true });
      setLastSent({ channel: 'email', count: 1, type: typeLabel(type), noEmail: 0 });
      return;
    }
    // Gmail Mailbox connected → send ONE personalised email per vendor
    // through team@pollendots.com. Hotels get "• Property: {name}" under
    // Location so chain reservation desks know exactly which property.
    if (mailboxReady) {
      const withEmail = [];
      const noEmailFallbacks = [];
      targets.forEach(v => (splitContacts(v.email).length ? withEmail : noEmailFallbacks).push(v));
      if (!withEmail.length) {
        setSending(false);
        toast.error('None of the picked vendors have an email on file — use WhatsApp.');
        return;
      }
      const t = localStorage.getItem('session_token');
      let sent = 0;
      const failedNames = [];
      for (const v of withEmail) {
        const to = splitContacts(v.email)[0];
        try {
          const r = await fetch(`${API}/admin/mailbox/send`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
            body: JSON.stringify({ to, subject, text: personaliseEmailBody(signedBody(message), v) }),
          });
          if (!r.ok) throw new Error();
          await log(v, 'email', to);
          sent += 1;
        } catch { failedNames.push(v.name || to); }
      }
      for (const v of noEmailFallbacks) await log(v, 'email', '(no-email — send WhatsApp)');
      if (sent) {
        toast.success(`Sent ${sent} personalised email${sent === 1 ? '' : 's'} via team@pollendots.com${noEmailFallbacks.length ? ` · ${noEmailFallbacks.length} no-email (send WhatsApp)` : ''}`);
      }
      if (failedNames.length) toast.error(`Send failed for: ${failedNames.join(', ')}`);
      setSending(false);
      onSent && onSent({ channel: 'email', count: sent, noEmail: noEmailFallbacks.length, viaMailbox: true });
      if (!compactMode) setPicked(new Set());
      setLastSent({ channel: 'email', count: sent, type: typeLabel(type), noEmail: noEmailFallbacks.length });
      return;
    }
    // BCC strategy (fallback when Gmail Mailbox isn't connected):
    //  • TO team@pollendots.com (mobile mail apps often refuse a mailto with
    //    an empty TO line — a key reason drafts "didn't open").
    //  • For every vendor with an email on file → BCC that email.
    //  • If ANY picked vendor lacks an email, also BCC the operator's own
    //    email so they get a record of what would have gone out. Those
    //    vendors are then expected to receive the same message via WhatsApp
    //    fired separately by the operator.
    const bccList = [];
    const sentWithEmail = [];
    const noEmailFallbacks = [];
    for (const v of targets) {
      const emails = splitContacts(v.email);
      if (emails.length) {
        bccList.push(emails[0]);
        await log(v, 'email', emails[0]);
        sentWithEmail.push(v);
      } else {
        noEmailFallbacks.push(v);
        // Log a placeholder so the journal still records intent.
        await log(v, 'email', '(no-email — operator BCC)');
      }
    }
    if (noEmailFallbacks.length && user?.email) {
      bccList.push(user.email);
    }
    if (sentWithEmail.length === 0 && noEmailFallbacks.length === 0) {
      setSending(false); toast.error('No vendors to email'); return;
    }
    const bodyText = personaliseEmailBodyMulti(signedBody(message), sentWithEmail);
    const url = `mailto:${encodeURIComponent('team@pollendots.com')}?bcc=${encodeURIComponent(bccList.join(','))}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`;
    if (url.length > 1900) {
      // Mail apps (especially mobile) silently drop long mailto: URLs —
      // fall back to copying everything so the operator can paste it.
      const clip = `BCC: ${bccList.join(', ')}\n\nSubject: ${subject}\n\n${bodyText}`;
      try { await navigator.clipboard.writeText(clip); } catch { /* best effort */ }
      toast.warning('This draft is too large for your mail app to open — recipients + message copied to clipboard. Paste into a new email, or connect Gmail in Mailbox for one-tap bulk sends.', { duration: 10000 });
    } else {
      window.location.href = url;
    }
    const parts = [];
    if (sentWithEmail.length) parts.push(`${sentWithEmail.length} BCC'd`);
    if (noEmailFallbacks.length) parts.push(`${noEmailFallbacks.length} no-email → operator BCC (send WhatsApp)`);
    toast.success(`Email opened · ${parts.join(' · ')}`);
    setSending(false);
    onSent && onSent({ channel: 'email', count: sentWithEmail.length, noEmail: noEmailFallbacks.length });
    // Keep the sheet OPEN so the operator can switch type (e.g. Drivers
    // next) and fire another round without re-navigating. Reset picks +
    // surface a "what just went out" strip.
    if (!compactMode) setPicked(new Set());
    setLastSent({ channel: 'email', count: sentWithEmail.length, type: typeLabel(type), noEmail: noEmailFallbacks.length });
  };

  const fireWhatsAppSequential = async () => {
    const targets = filtered.filter(v => picked.has(v.id));
    if (!targets.length) { toast.error('Pick at least one vendor'); return; }
    if (!message.trim()) { toast.error('Type a message first'); return; }
    setSending(true);
    // Open one wa.me per vendor — the user taps Send in WhatsApp for each.
    let opened = 0;
    for (const v of targets) {
      const phones = splitContacts(v.phone);
      if (!phones.length) continue;
      const digits = digitsOnly(phones[0]);
      if (!digits) continue;
      const url = `https://wa.me/${digits}?text=${encodeURIComponent(personaliseWhatsAppText(signedBody(message), v))}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      await log(v, 'whatsapp', phones[0]);
      opened += 1;
      // Tiny stagger so browser pop-up blockers don't choke
      await new Promise(r => setTimeout(r, 250));
    }
    if (opened === 0) toast.error('None of the picked vendors have a phone on file');
    else toast.success(`Opened ${opened} WhatsApp chats — logged. Tap Send in each.`);
    setSending(false);
    onSent && onSent({ channel: 'whatsapp', count: opened });
    // Keep open for the next round (see fireEmailBulk for rationale)
    setPicked(new Set());
    setLastSent({ channel: 'whatsapp', count: opened, type: typeLabel(type) });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="z-[80] max-h-[92vh] overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+5rem)] lg:pb-6 rounded-t-2xl" data-testid="bulk-outreach-sheet">
        <SheetHeader>
          <SheetTitle className="text-base">
            {compactMode ? 'Send to selected vendors' : 'Bulk vendor outreach'}
          </SheetTitle>
          <SheetDescription className="text-[11px]">
            {compactMode
              ? `Vendors picked inside the requirement card. Fire WhatsApp (sequential) or Email (single BCC). Every send is logged.`
              : `Filter vendors → pick → write once → fire WhatsApp (sequential) or Email (single BCC). Every send is logged.`}
            {queryId && <span className="ml-1 text-amber-700">Attached to current query.</span>}
          </SheetDescription>
        </SheetHeader>

        {/* "What just went out" strip — appears after each batch so the
            operator can immediately switch vendor type and fire another
            round without leaving the sheet. */}
        {lastSent && lastSent.count > 0 && (
          <div className="mt-3 p-2 rounded-md border border-emerald-300 bg-emerald-50 text-xs text-emerald-900 flex items-center justify-between" data-testid="bulk-last-sent">
            <span>
              ✓ Just sent <strong>{lastSent.channel}</strong> to <strong>{lastSent.count}</strong> {lastSent.type}.
              <span className="ml-1 text-emerald-800/70">Change Vendor Type to send to another set.</span>
            </span>
            <button className="text-emerald-700 hover:text-emerald-900" onClick={() => setLastSent(null)} aria-label="Dismiss">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Filters — hidden in compact mode (vendors already chosen inside
            the requirement card). */}
        {!compactMode && (<>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Vendor type</Label>
            <Select value={type || 'all'} onValueChange={v => setType(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="bulk-type"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {VENDOR_TYPE_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Location contains</Label>
            <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="Kochi, Spiti…" className="h-9 text-sm" data-testid="bulk-location" />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, phone, email" className="pl-7 h-9 text-sm" data-testid="bulk-search" />
            </div>
          </div>
        </div>

        {/* Location aliases — operator can OR-target additional names so a
            single outreach reaches vendors in nearby/alternate locations
            (Cherrapunjee / Sohra, Jaigaon / Phuentsholing, etc.). */}
        {locationAliases.length > 0 && (
          <div className="mt-2">
            <Label className="text-[11px] text-muted-foreground">Also matching</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="bulk-location-aliases">
              {locationAliases.map(a => (
                <span key={a} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-muted border">
                  {a}
                  <button type="button" onClick={() => setLocationAliases(locationAliases.filter(x => x !== a))}
                    className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${a}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Accommodation type filter — only meaningful when filtering Hotels.
            Multi-select chip row pre-filled from the requesting Hotel block
            so the operator sees only matching properties (Resort, Villa…). */}
        {type === 'Hotel' && (
          <div className="mt-2" data-testid="bulk-acc-filter">
            <Label className="text-xs">Accommodation type filter</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {ACCOMMODATION_TYPES.map(a => {
                const active = accommodationFilter.includes(a);
                return (
                  <button key={a} type="button"
                    onClick={() => setAccommodationFilter(active
                      ? accommodationFilter.filter(x => x !== a)
                      : [...accommodationFilter, a])}
                    className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                      active ? 'bg-foreground text-background border-foreground'
                             : 'bg-white text-foreground border-border'
                    }`}
                    data-testid={`bulk-acc-${a.toLowerCase().replace(/\s+/g, '-')}`}>
                    {a}
                  </button>
                );
              })}
              {accommodationFilter.length > 0 && (
                <button type="button" className="text-[11px] text-muted-foreground underline ml-1"
                  onClick={() => setAccommodationFilter([])}>
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
        </>)}

        {/* Vendor list */}
        <div className="mt-3 border rounded-lg">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
            <div className="text-xs">
              {loading
                ? 'Loading…'
                : compactMode
                  ? `${filtered.length} vendor${filtered.length === 1 ? '' : 's'} from card`
                  : `${filtered.length} vendor${filtered.length === 1 ? '' : 's'} match`}
              {picked.size > 0 && <span className="ml-2 text-amber-700">· {picked.size} picked</span>}
            </div>
            {!compactMode && (
              <Button size="sm" variant="ghost" onClick={toggleAll} className="h-7 text-xs" data-testid="bulk-toggle-all">
                {picked.size === filtered.length && filtered.length > 0 ? 'Unpick all' : 'Pick all'}
              </Button>
            )}
          </div>
          <div className={compactMode ? 'max-h-72 overflow-y-auto' : 'max-h-56 overflow-y-auto'}>
            {loading ? (
              <div className="text-xs text-muted-foreground flex items-center gap-2 p-3"><Loader2 className="w-3 h-3 animate-spin" />Loading vendors…</div>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground p-3">No vendors match these filters.</p>
            ) : filtered.map(v => {
              const emails = splitContacts(v.email);
              const phones = splitContacts(v.phone);
              return (
                <label key={v.id} className="flex items-start gap-2 p-2 border-b last:border-b-0 hover:bg-muted/30 cursor-pointer" data-testid={`bulk-vendor-${v.id}`}>
                  <Checkbox checked={picked.has(v.id)} onCheckedChange={() => toggle(v.id)} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{v.name || '—'}</div>
                    <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-2">
                      <Badge variant="outline" className="text-[10px] h-4">{v.type}</Badge>
                      {v.location && <span>{v.location}</span>}
                      {phones.length > 0 && <span>· {phones.length} phone</span>}
                      {emails.length > 0 && <span>· {emails.length} email</span>}
                      {phones.length === 0 && emails.length === 0 && <span className="text-red-600">· no contact</span>}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Composer */}
        <div className="mt-3 space-y-2">
          {!compactMode && (
            <div className="p-2 rounded-md border bg-muted/20 space-y-1.5" data-testid="bulk-rate-template">
              <Label className="text-xs">Season / month / year (for B2B rate requests)</Label>
              <div className="flex gap-2">
                <Input value={season} onChange={e => setSeason(e.target.value)}
                  placeholder="e.g. Nov 2026 – Feb 2027 · Full year 2027"
                  className="h-9 text-sm flex-1" data-testid="bulk-season" />
                <Button type="button" size="sm" variant="outline" className="h-9 text-xs whitespace-nowrap"
                  onClick={fillRateTemplate} data-testid="bulk-fill-template">
                  Fill rate request
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Builds a rate-request message from the Location filter + period above. Each hotel's property name is added automatically on send.
              </p>
            </div>
          )}
          <div>
            <Label className="text-xs">Subject (email only)</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} className="h-9 text-sm" data-testid="bulk-subject" />
          </div>
          <div>
            <Label className="text-xs">Message — signature auto-appended</Label>
            <Textarea rows={5} value={message} onChange={e => setMessage(e.target.value)} placeholder="E.g. Quote needed for a Sumo from Kochi airport to Munnar — 18 Aug drop, 22 Aug pickup, 4 pax + luggage. Share rate, vehicle photo, driver name." data-testid="bulk-body" />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Signed as: <strong>{operatorName || user?.name || 'Operator'}</strong> · Team Pollen Dots
            </p>
            {/* Optional attachment links — operator decides when to attach
                permits / ID / itinerary. Click appends the public branded
                URLs to the message. Idempotent: appended block is keyed by
                a marker so subsequent clicks replace, not duplicate. */}
            {(attachments || []).filter(a => a.share_slug && !a.is_deleted).length > 0 && (
              <Button type="button" size="sm" variant="outline"
                className="mt-2 h-8 border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                data-testid="bulk-attach-docs"
                onClick={() => {
                  const downloadable = (attachments || []).filter(a => a.share_slug && !a.is_deleted);
                  if (!downloadable.length) return;
                  const labelMap = { itinerary: 'Itinerary', id_proof: 'ID proof', passport: 'Passport',
                                     age_proof: 'Age proof', visa: 'Visa', permit: 'Customer Documents for Permits', other: 'Customer Documents' };
                  const grouped = {};
                  downloadable.forEach(a => { const k = a.kind || 'other'; (grouped[k] = grouped[k] || []).push(a); });
                  const block = ['', 'Attachments (click to download):'];
                  Object.entries(grouped).forEach(([k, arr]) => {
                    arr.forEach(a => {
                      // Itineraries get the branded customer-landing URL
                      // (`/connection/itinerary/<slug>`) — same link we share
                      // with the customer. Other docs (passports, permits,
                      // ID proofs) use the raw public download endpoint.
                      const u = k === 'itinerary'
                        ? `${shareBase}/connection/itinerary/${a.share_slug}`
                        : `${shareBase}/api/pollen/public/query-attachments/${a.share_slug}/download`;
                      // Single-line entry — label + URL together so the
                      // vendor's eye lands on the clickable link.
                      block.push(`  • ${labelMap[k] || k}: ${u}`);
                    });
                  });
                  // Marker keeps idempotency: strip the previous appended
                  // block before adding the fresh one.
                  const MARKER = '\nAttachments (click to download):';
                  const stripped = message.includes(MARKER)
                    ? message.split(MARKER)[0].trimEnd()
                    : message;
                  setMessage(`${stripped}\n${block.join('\n')}`);
                  toast.success(`Appended ${downloadable.length} document link${downloadable.length === 1 ? '' : 's'}`);
                }}>
                <Mail className="w-3 h-3 mr-1" />
                Attach customer documents ({(attachments || []).filter(a => a.share_slug && !a.is_deleted).length})
              </Button>
            )}
          </div>
        </div>

        {/* Fire buttons */}
        <p className="mt-2 text-[10px] text-muted-foreground" data-testid="bulk-email-mode-hint">
          {mailboxReady
            ? '✓ Gmail connected — Email sends one personalised mail per vendor via team@pollendots.com (property name auto-added for hotels).'
            : 'Email opens a single BCC draft (property names listed for hotels). Connect Gmail in Mailbox to auto-send one personalised email per property.'}
        </p>
        <div className="mt-1 flex flex-wrap gap-2 sticky bottom-0 bg-background pt-2">
          <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} className="ml-auto" data-testid="bulk-cancel">
            <X className="w-4 h-4 mr-1" />Done
          </Button>
          <Button size="sm" variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100" onClick={fireWhatsAppSequential} disabled={sending || picked.size === 0} data-testid="bulk-send-whatsapp">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Megaphone className="w-4 h-4 mr-1" />}
            WhatsApp ({picked.size})
          </Button>
          <Button size="sm" onClick={fireEmailBulk} disabled={sending || picked.size === 0} data-testid="bulk-send-email">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
            {mailboxReady ? 'Email' : 'Email BCC'} ({picked.size})
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
